'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  dynamicFormQueries,
  DynamicFormFieldInput
} from '@/features/dynamic-forms';
import type { DynamicFormField } from '@/features/dynamic-forms';
import {
  containerTypeKeys,
  containerTypesApi
} from '@/features/container-types';
import { buildRegistrationFormSchema } from '../schemas';
import {
  useCreateRegistrationMutation,
  useUpdateRegistrationMutation,
  useAdvanceStageMutation
} from '../services/registration.mutations';
import type { Registration, RegistrationStage } from '../types';

interface RegistrationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registration?: Registration | null;
  formId?: number;
  /** Stage this dialog completes after a rejection fix (the server picks the next). */
  advanceFrom?: RegistrationStage;
}

export function RegistrationFormDialog({
  open,
  onOpenChange,
  registration,
  formId,
  advanceFrom
}: RegistrationFormDialogProps) {
  const t = useTranslations('registration');
  const tv = useTranslations('validation');
  const isEditing = !!registration;

  const {
    data: activeFormData,
    isLoading: activeLoading,
    isError: activeError
  } = useQuery({
    ...dynamicFormQueries.activeByType('registration'),
    enabled: open && !formId
  });

  const {
    data: specificFormData,
    isLoading: specificLoading,
    isError: specificError
  } = useQuery({
    ...dynamicFormQueries.detail(formId ?? 0),
    enabled: open && !!formId
  });

  const formData = formId ? specificFormData : activeFormData;
  const isLoading = formId ? specificLoading : activeLoading;
  const isError = formId ? specificError : activeError;

  const containerTypesSelect = useInfiniteSelect({
    queryKey: containerTypeKeys.lists(),
    queryFn: (params) => containerTypesApi.getAll(params),
    mapOption: useCallback(
      (ct: { id: number; name: string }) => ({
        value: String(ct.id),
        label: ct.name
      }),
      []
    ),
    params: { is_active: true },
    enabled: open
  });

  const dynamicForm = formData?.data ?? formData;
  const fields = useMemo<DynamicFormField[]>(
    () => (dynamicForm as any)?.fields ?? [],
    [dynamicForm]
  );

  const schema = useMemo(
    () => buildRegistrationFormSchema(fields, tv),
    [fields, tv]
  );

  const defaultValues = useMemo(() => {
    const formResponses: Record<string, unknown> = {};
    for (const field of fields) {
      const key = String(field.id);
      switch (field.type) {
        case 'yes_no':
          formResponses[key] = false;
          break;
        case 'selection_list':
          formResponses[key] = field.default_value ?? '';
          break;
        default:
          formResponses[key] = '';
      }
    }
    return {
      container_type_id: '' as unknown as number,
      form_responses: formResponses
    };
  }, [fields]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur'
  });

  const createMutation = useCreateRegistrationMutation();
  const updateMutation = useUpdateRegistrationMutation();
  const advanceMutation = useAdvanceStageMutation();
  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    advanceMutation.isPending;

  useEffect(() => {
    if (!open || fields.length === 0) return;

    if (registration) {
      const raw = registration.form_responses;
      const normalized: Record<string, unknown> = raw ? { ...raw } : {};

      const formResponses: Record<string, unknown> = {};
      for (const field of fields) {
        const key = String(field.id);
        const saved = normalized[key];
        if (saved != null) {
          formResponses[key] = saved;
        } else {
          formResponses[key] = field.type === 'yes_no' ? false : '';
        }
      }
      form.reset({
        container_type_id: registration.container_type_id
          ? (String(registration.container_type_id) as unknown as number)
          : ('' as unknown as number),
        form_responses: formResponses
      });
    } else {
      form.reset(defaultValues);
    }
  }, [open, registration, fields, form, defaultValues]);

  async function onSubmit(values: Record<string, unknown>) {
    if (!dynamicForm) return;

    const payload = {
      dynamic_form_id: (dynamicForm as any).id,
      container_type_id: Number((values as any).container_type_id),
      form_responses: (values as any).form_responses
    };

    if (isEditing && registration) {
      await updateMutation.mutateAsync({
        id: registration.id,
        data: payload
      });

      // If rejected back to registration, advance directly to review
      if (
        registration.rejected_to_stage === 'registration' &&
        registration.current_stage === 'registration' &&
        advanceFrom
      ) {
        await advanceMutation.mutateAsync({
          id: registration.id,
          data: { from: advanceFrom }
        });
      }
    } else {
      await createMutation.mutateAsync(payload);
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[700px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editRegistration') : t('createRegistration')}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className='space-y-4'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className='space-y-2'>
                <Skeleton className='h-4 w-32' />
                <Skeleton className='h-10 w-full' />
              </div>
            ))}
          </div>
        )}

        {!isLoading && (isError || fields.length === 0) && (
          <div className='flex flex-col items-center gap-3 py-8 text-center'>
            <AlertCircle className='text-muted-foreground h-10 w-10' />
            <p className='text-sm font-medium'>{t('noLabFormTitle')}</p>
            <p className='text-muted-foreground max-w-sm text-xs'>
              {t('noLabFormHint')}
            </p>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
          </div>
        )}

        {!isLoading && fields.length > 0 && (
          <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <div className='space-y-4'>
              <FormCombobox
                control={form.control}
                name='container_type_id'
                label={t('containerType')}
                placeholder={t('containerTypePlaceholder')}
                infiniteConfig={containerTypesSelect}
                required
              />

              {fields.map((field) => (
                <DynamicFormFieldInput
                  key={field.id}
                  field={field}
                  control={form.control}
                  name={`form_responses.${String(field.id)}`}
                />
              ))}

              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => onOpenChange(false)}
                >
                  {t('cancel')}
                </Button>
                <Button type='submit' disabled={isPending}>
                  {isPending && (
                    <Loader2 className='me-2 h-4 w-4 animate-spin' />
                  )}
                  {isEditing ? t('saveChanges') : t('create')}
                </Button>
              </DialogFooter>
            </div>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
